import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Chip,
	Grid,
	Tooltip,
	Typography,
} from '@mui/material';
import moment from 'moment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { Response } from '../models/nps';
import { Pgap } from '@nps.today/component-library';
import { Rating } from '@nps.today/component-library';

function tryParseJson(json: any) {
	try {
		const parsed = JSON.parse(json);
		const type = typeof parsed;
		//only allow objects.
		if (type === 'object') return parsed;
		return null;
	} catch {
		return null;
	}
}

//generates caller list on the datagrid that represent the multiple choice result
function getAnswers(props: any) {
	let callerAnswers = [
		props.answer1,
		props.answer2,
		props.answer3,
		props.answer4,
		props.answer5,
	].map((a) => tryParseJson(a));
	const questions = [
		callerAnswers[0] && callerAnswers[0].question
			? callerAnswers[0].question
			: 'question 1',
		callerAnswers[1] && callerAnswers[1].question
			? callerAnswers[1].question
			: 'question 2',
		callerAnswers[2] && callerAnswers[2].question
			? callerAnswers[2].question
			: 'question 3',
		callerAnswers[3] && callerAnswers[3].question
			? callerAnswers[3].question
			: 'question 4',
		callerAnswers[4] && callerAnswers[4].question
			? callerAnswers[4].question
			: 'question 5',
	] as any;
	//if caller has no answers - return.
	if (callerAnswers.every((val: any) => val === null || val === undefined))
		return;
	//do not generate the lists if no answer was found between caller and agent
	return (
		<>
			{/*list containing caller answers on multiple choice*/}
			<List style={{ paddingTop: '0px', paddingBottom: '0px' }}>
				{callerAnswers &&
					callerAnswers.map((value, key) => {
						return (
							value && (
								<ListItem
									style={{
										paddingTop: '0px',
										paddingBottom: '0px',
										display: 'block',
									}}
								>
									<ListItemText
										primary={handlePrimaryTitle(value.answer)}
										secondary={handleSecondaryTitle(questions[key])}
									/>
								</ListItem>
							)
						);
					})}
			</List>
		</>
	);
}

//used for shortening a title. The tooltip will show the full title.
function getTitleWithTooltip(maxLength: number, title: any) {
	return (
		<Tooltip title={title} placement="top-start">
			<span>{title.substring(0, maxLength + 1) + '...'}</span>
		</Tooltip>
	);
}

function handlePrimaryTitle(answer: any) {
	if (!answer) return;
	let answerTooLong = false;
	const maxLength = 26;
	answer.sort(); //sort alphabetically
	const seperateByComma = answer.map((v: string, index: number) => {
		return index < answer.length - 1 ? v + ', ' : v;
	});
	if (seperateByComma.length > maxLength) answerTooLong = true;
	return answerTooLong
		? getTitleWithTooltip(maxLength, seperateByComma)
		: seperateByComma;
}

//secondary title is the question that was asked for an agent or caller
function handleSecondaryTitle(question: any) {
	const maxLength = 30;
	const questionTooLong = question.length > maxLength;
	if (!questionTooLong) return question;
	return getTitleWithTooltip(maxLength, question);
}

interface ResponseProps {
	response: Response;
	showPgapRating: boolean;
	showRespondentPhone: boolean;
}

// TODO chat icon - add campaign name to response

function ResponseDetails({
	response,
	showPgapRating,
	showRespondentPhone,
}: ResponseProps) {
	return (
		<Accordion>
			<AccordionSummary
				expandIcon={response.respondentComment && <ExpandMoreIcon />}
				aria-controls="panel1a-content"
				id="panel1a-header"
			>
				<Grid
					container
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Grid
						container
						item
						xs
						direction="row"
						justifyContent="flex-start"
						alignItems="center"
					>
						<div className="container">
							{(showPgapRating && response.agentRating) ||
							response.agentRating === 0 ? (
								<Pgap
									agentRating={response.agentRating}
									rating={response.rating}
									pgapScoreText={'P-Gap'}
									agentRatingText={'Agent Rating'}
									respondentRatingText={'Caller Rating'}
									justifyContent="flex-start"
								/>
							) : (
								<div style={{ width: '100%' }}>
									<Rating rating={response.rating} />
								</div>
							)}

							{getAnswers({ ...response, showPgapRating: true })}
							<Typography>
								{`
							${moment(response.time).fromNow()}
							`}
								<Box px={7} />
							</Typography>
							{response.category && (
								<Chip size="small" label={response.category} color="primary" />
							)}
						</div>
					</Grid>
				</Grid>
			</AccordionSummary>
			{showRespondentPhone && (
				<AccordionDetails>
					<Typography>{response.respondentPhoneNumber}</Typography>
				</AccordionDetails>
			)}
			{response.respondentComment && (
				<AccordionDetails>
					<Typography>{response.respondentComment}</Typography>
				</AccordionDetails>
			)}
		</Accordion>
	);
}

export default ResponseDetails;
